/**
 * Componente ChaptersSection
 * Sección de selección de capítulos con grid de tarjetas
 */
import React, { memo, useCallback } from "react";
import { motion } from "framer-motion";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ComicButton from "../../shared/components/ComicButton/ComicButton";

// Componente ChapterCard memoizado para evitar re-renders innecesarios
const ChapterCard = memo(({ comic, currentLanguage, backendBaseUrl, onSelect }) => (
    <motion.div
        className={`relative p-4 rounded-lg shadow-lg ${comic.available ? "cursor-pointer text-white bg-gray-800" : "cursor-pointer text-gray-300 bg-gray-500"
            }`}
        style={{
            backgroundImage: comic.available ? `url(${backendBaseUrl}${comic.background})` : "none",
            backgroundSize: "cover",
            backgroundPosition: "center",
        }}
        initial={{ scale: 0.9 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 1 }}
        onClick={() => onSelect(comic)}
    >
        <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg"></div>
        <div className="relative z-10 flex flex-col items-center justify-center h-full h-20">
            <h3 className="text-xl font-semibold">
                <Trans i18nKey="chapter" /> {comic.id.split("-")[1]}: {comic.title[currentLanguage]}
            </h3>
            {!comic.available && (
                <p className="text-sm italic mt-2">
                    <Trans i18nKey="suscribe" />
                </p>
            )}
        </div>
    </motion.div>
));

ChapterCard.displayName = "ChapterCard";

const ChaptersSection = ({ comics, currentLanguage, backendBaseUrl, goTo }) => {
    const navigate = useNavigate();

    // Memoizar callback para evitar re-renders de ChapterCard
    const handleSelectChapter = useCallback((comic) => {
        if (comic.available) {
            goTo(`${comic.id}-opciones`);
        } else {
            goTo("suscripcion");
        }
    }, [goTo]);

    return (
        <motion.div
            key="capitulos"
            className="absolute inset-0 flex flex-col items-center justify-center text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
        >
            <h2 className="text-4xl font-bold text-white mb-4">
                <Trans i18nKey="selectChapter" />
            </h2>
            <div
                className="grid grid-cols-1 md:grid-cols-3 gap-6 w-auto px-4 overflow-y-auto md:overflow-visible"
                style={{ maxHeight: "calc(100dvh - 200px)", WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }}
            >
                {comics.map((comic) => (
                    <ChapterCard
                        key={comic.id}
                        comic={comic}
                        currentLanguage={currentLanguage}
                        backendBaseUrl={backendBaseUrl}
                        onSelect={handleSelectChapter}
                    />
                ))}
            </div>
            <div className="mt-4">
                <ComicButton
                    buttonName={<Trans i18nKey="backToMenu" />}
                    hoverBtnName={<Trans i18nKey="backToMenu" />}
                    handleButtonClick={() => navigate(-1)}
                />
            </div>
        </motion.div>
    );
};

export default memo(ChaptersSection);
